import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Inbox, RefreshCcw, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";
import { CANALES, TIPOS_CORREO } from "@/lib/constants";
import { formatHoraIngreso } from "@/lib/agenda";

const VISTAS = [
  { valor: "bokun_sin_reserva", label: "Sin reserva sincronizada (Bókun)" },
  { valor: "otros", label: "Otros correos sin reserva" },
];

function EmailRow({ item, checked, onCheck, onReprocesar, busy }) {
  const [abierto, setAbierto] = useState(false);
  const ids = item.identificadores || {};
  const idsTexto = Object.values(ids).filter(Boolean).join(" · ");
  const motivo = item.motivo_omision || item.motivo_accion || item.motivo_revision
    || item.parsed?.mensaje_texto || "Sin motivo registrado.";

  return (
    <Card className="p-3" data-testid={`sin-reserva-row-${item.id}`}>
      <div className="flex items-start gap-3">
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onCheck(item.id, !!v)}
          className="mt-1"
          data-testid={`sin-reserva-checkbox-${item.id}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {item.tipo_correo && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border bg-slate-100 text-slate-700 border-slate-200">
                {TIPOS_CORREO[item.tipo_correo] || item.tipo_correo}
              </span>
            )}
            {item.canal && <span className="text-[11px] text-slate-400">{CANALES[item.canal] || item.canal}</span>}
            {item.buzon_email && <span className="text-[11px] text-slate-400">{item.buzon_email}</span>}
            {idsTexto && <span className="font-mono text-[11px] text-slate-500">{idsTexto}</span>}
            <span className="text-[10px] text-slate-400" data-testid={`sin-reserva-ingreso-${item.id}`}>
              Recibido {formatHoraIngreso(item.fecha_recibido || item.created_at)}
            </span>
          </div>
          <p className="text-sm font-medium text-slate-900 mt-1 truncate">{item.asunto || "(sin asunto)"}</p>
          <p className="text-[11px] text-slate-400">{item.remitente}</p>
          <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
            {motivo}
          </p>
          <button
            onClick={() => setAbierto((v) => !v)}
            data-testid={`sin-reserva-ver-completo-${item.id}`}
            className="mt-2 inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-800"
          >
            {abierto ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Ver correo completo
          </button>
          {abierto && (
            <pre className="mt-2 text-[12px] leading-relaxed text-slate-700 whitespace-pre-wrap font-sans bg-slate-50 border border-slate-100 rounded px-3 py-2 max-h-56 overflow-y-auto">
              {item.cuerpo}
            </pre>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onReprocesar([item.id])}
          disabled={busy}
          data-testid={`sin-reserva-reprocesar-${item.id}`}
          className="gap-1.5 shrink-0"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
          Reprocesar
        </Button>
      </div>
    </Card>
  );
}

export default function SinReservaTab({ onCount }) {
  const [vista, setVista] = useState("bokun_sin_reserva");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalesBokun, setTotalesBokun] = useState(null);
  const [mostrarSincronizados, setMostrarSincronizados] = useState(false);
  const [loading, setLoading] = useState(true);
  const [buzones, setBuzones] = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [buzonFiltro, setBuzonFiltro] = useState("todos");
  const [page, setPage] = useState(1);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [reprocesandoIds, setReprocesandoIds] = useState(new Set());
  const [loteEnCurso, setLoteEnCurso] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      if (vista === "bokun_sin_reserva") {
        const { data } = await api.get("/captura/emails-sin-reserva/auditoria-bokun", { params, timeout: 30000 });
        setItems(data.sin_reserva || []);
        setTotal(data.totales?.sin_reserva || 0);
        setTotalesBokun(data.totales || null);
        onCount?.((data.totales?.sin_reserva || 0));
      } else {
        if (buzonFiltro !== "todos") params.buzon_email = buzonFiltro;
        const { data } = await api.get("/captura/emails-sin-reserva", { params, timeout: 20000 });
        setItems(data.items || []);
        setTotal(data.total || 0);
        setTotalesBokun(null);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [page, desde, hasta, buzonFiltro, vista, onCount]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/captura/buzones").then(({ data }) => setBuzones(data || [])).catch(() => {});
  }, []);

  const toggleSel = (id, val) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (val) next.add(id); else next.delete(id);
      return next;
    });
  };

  const reprocesar = async (ids) => {
    const marcarBusy = (busy) => setReprocesandoIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (busy ? next.add(id) : next.delete(id)));
      return next;
    });
    marcarBusy(true);
    if (ids.length > 1) setLoteEnCurso(true);
    try {
      const { data } = await api.post("/captura/emails-sin-reserva/reprocesar", { ids });
      const partes = [];
      if (data.reserva_creada) partes.push(`${data.reserva_creada} creó reserva`);
      if (data.bandeja) partes.push(`${data.bandeja} a Bandeja`);
      if (data.acciones) partes.push(`${data.acciones} a Acciones`);
      if (data.sin_generar_nada) partes.push(`${data.sin_generar_nada} sin generar nada`);
      if (data.fallidas) partes.push(`${data.fallidas} con error`);
      toast[data.fallidas ? "warning" : "success"](
        `Reproceso de ${data.total}: ${partes.join(" · ") || "sin cambios"}.`
      );
      setSeleccionados(new Set());
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      marcarBusy(false);
      setLoteEnCurso(false);
    }
  };

  const limpiarFiltros = () => { setDesde(""); setHasta(""); setBuzonFiltro("todos"); setPage(1); };
  const cambiarVista = (v) => { setVista(v); setPage(1); setSeleccionados(new Set()); };

  return (
    <div className="space-y-3" data-testid="sin-reserva-tab">
      <p className="text-[12px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
        Correos que la app leyó y procesó, pero que NO terminaron en ninguna reserva ni quedaron esperando en
        Acciones. Revisalos y usá <strong>"Reprocesar"</strong> para intentarlo de nuevo -- no se pierden.
      </p>

      <div className="flex flex-wrap gap-2" data-testid="sin-reserva-vistas">
        {VISTAS.map((v) => (
          <button
            key={v.valor}
            onClick={() => cambiarVista(v.valor)}
            data-testid={`sin-reserva-vista-${v.valor}`}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors ${
              vista === v.valor
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {vista === "bokun_sin_reserva" && totalesBokun && (
        <button
          onClick={() => setMostrarSincronizados((v) => !v)}
          data-testid="sin-reserva-toggle-sincronizados"
          className="flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-700"
        >
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
          {totalesBokun.con_reserva} omitido(s) por Bókun ya sincronizados (no requieren nada)
          {totalesBokun.sin_identificadores > 0 && ` · ${totalesBokun.sin_identificadores} avisos sin identificadores legibles`}
          {totalesBokun.no_avisos_bokun > 0 && ` · ${totalesBokun.no_avisos_bokun} correos de la cuenta sin identificadores de reserva (no son avisos)`}
          {mostrarSincronizados ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      )}
      {vista === "bokun_sin_reserva" && mostrarSincronizados && totalesBokun && (
        <Card className="p-3 text-[12px] text-slate-500 bg-emerald-50 border-emerald-200" data-testid="sin-reserva-sincronizados-detalle">
          Estos correos se saltearon porque se detectó que la cuenta o el aviso ya estaba sincronizado por la API
          de Bókun -- verificado contra la base actual. No es trabajo pendiente, no se muestran en el listado.
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500">Rango:</span>
        <Input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setPage(1); }}
              className="w-auto" data-testid="sin-reserva-desde" />
        <span className="text-slate-400">a</span>
        <Input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setPage(1); }}
              className="w-auto" data-testid="sin-reserva-hasta" />
        {vista === "otros" && (
          <Select value={buzonFiltro} onValueChange={(v) => { setBuzonFiltro(v); setPage(1); }}>
            <SelectTrigger className="w-56" data-testid="sin-reserva-buzon-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los buzones</SelectItem>
              {buzones.map((b) => (
                <SelectItem key={b.id} value={b.email}>{b.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {(desde || hasta || buzonFiltro !== "todos") && (
          <Button variant="ghost" size="sm" onClick={limpiarFiltros} className="text-slate-500">
            Limpiar filtros
          </Button>
        )}
        <span className="ml-auto text-sm text-slate-600" data-testid="sin-reserva-total">
          {loading ? "Cargando…" : `${total} correo(s)`}
        </span>
        <Button
          size="sm"
          onClick={() => reprocesar(Array.from(seleccionados))}
          disabled={seleccionados.size === 0 || loteEnCurso}
          data-testid="sin-reserva-reprocesar-seleccionados"
          className="gap-1.5"
        >
          {loteEnCurso ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
          Reprocesar seleccionados ({seleccionados.size})
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-slate-400 text-sm" data-testid="sin-reserva-empty">
          <Inbox className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          No hay correos en este rango. ¡Todo está en algún lado!
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <EmailRow
              key={item.id}
              item={item}
              checked={seleccionados.has(item.id)}
              onCheck={toggleSel}
              onReprocesar={reprocesar}
              busy={reprocesandoIds.has(item.id)}
            />
          ))}
        </div>
      )}

      {total > limit && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                 data-testid="sin-reserva-pagina-anterior">
            Anterior
          </Button>
          <span className="text-xs text-slate-500">Página {page} de {Math.ceil(total / limit)}</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / limit)}
                 onClick={() => setPage((p) => p + 1)} data-testid="sin-reserva-pagina-siguiente">
            Siguiente
          </Button>
        </div>
      )}
    </div>
  );
}
